import { RefObject, useCallback, useEffect, useRef } from "react";
import { listen } from "./listen";

/** How long the list has to be still before the reader counts as having stopped scrolling. */
const SETTLE_MS = 150;

/**
 * Returns a function telling whether the reader is scrolling the container: a finger on it, or the
 * list still moving from a fling.
 */
export const useScrollActivity = (containerRef: RefObject<HTMLElement>) => {
  const touching = useRef(false);
  const lastScrollAt = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const stopListening = [
      listen(container, ["touchstart"], () => {
        touching.current = true;
      }),
      listen(container, ["touchend", "touchcancel"], () => {
        touching.current = false;
        // A fling may follow, and its first scroll event can be a frame away.
        lastScrollAt.current = performance.now();
      }),
      listen(container, ["scroll"], () => {
        lastScrollAt.current = performance.now();
      }),
    ];
    return () => stopListening.forEach((stop) => stop());
  }, [containerRef]);

  return useCallback(
    () =>
      touching.current || performance.now() - lastScrollAt.current < SETTLE_MS,
    []
  );
};
